import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { portalTournamentAPI } from '../../../services/api';
import { isTabEditable } from '../../../utils/tournamentPhases';

/**
 * PortalFeedbackTab - Post-round questionnaire for players.
 * Editable when portalStatus === 'completed', read-only otherwise.
 */
const PortalFeedbackTab = () => {
  const { tournamentId } = useParams();
  const { tournament, portalStatus } = useOutletContext();

  const [existingFeedback, setExistingFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    overallRating: 0,
    courseCondition: { rating: 0, comment: '' },
    paceExperience: { rating: 0, comment: '' },
    tournamentOrganization: { rating: 0, comment: '' },
    generalComments: '',
  });

  const editable = isTabEditable('feedback', portalStatus);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await portalTournamentAPI.getMyFeedback(tournamentId);
        const data = res.data?.data?.feedback || [];
        setExistingFeedback(data);
        if (data.length > 0) {
          setSubmitted(true);
        }
      } catch (err) {
        console.error('Failed to load feedback:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [tournamentId]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const entries = [];

      if (form.overallRating > 0) {
        entries.push({
          type: 'overall-rating',
          rating: form.overallRating,
          content: 'Overall rating',
        });
      }

      if (form.courseCondition.rating > 0 || form.courseCondition.comment.trim()) {
        entries.push({
          type: 'course-condition',
          rating: form.courseCondition.rating || undefined,
          content: form.courseCondition.comment.trim() || `Rating: ${form.courseCondition.rating}/5`,
        });
      }

      if (form.paceExperience.rating > 0 || form.paceExperience.comment.trim()) {
        entries.push({
          type: 'pace-experience',
          rating: form.paceExperience.rating || undefined,
          content: form.paceExperience.comment.trim() || `Rating: ${form.paceExperience.rating}/5`,
        });
      }

      if (form.tournamentOrganization.rating > 0 || form.tournamentOrganization.comment.trim()) {
        entries.push({
          type: 'tournament-organization',
          rating: form.tournamentOrganization.rating || undefined,
          content: form.tournamentOrganization.comment.trim() || `Rating: ${form.tournamentOrganization.rating}/5`,
        });
      }

      if (form.generalComments.trim()) {
        entries.push({
          type: 'general',
          content: form.generalComments.trim(),
        });
      }

      if (entries.length === 0) return;

      await portalTournamentAPI.submitFeedback(tournamentId, { entries });
      setSubmitted(true);
      setExistingFeedback(entries);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange, disabled = false }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star === value ? 0 : star)}
          className={`text-2xl transition-colors ${
            star <= value ? 'text-yellow-400' : 'text-gray-300'
          } ${disabled ? 'cursor-default' : 'cursor-pointer hover:text-yellow-300'}`}
        >
          &#9733;
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-6 w-6 border-4 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Read-only view of submitted feedback
  if (submitted && existingFeedback.length > 0) {
    return (
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h3 className="font-semibold text-green-800 mb-1">Feedback Submitted</h3>
          <p className="text-sm text-green-600">Thank you for your feedback!</p>
        </div>

        {existingFeedback.map((entry, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">
              {(entry.type || '').replace(/-/g, ' ')}
            </p>
            {entry.rating && (
              <div className="text-yellow-400 mb-1">
                {'★'.repeat(entry.rating)}{'☆'.repeat(5 - entry.rating)}
              </div>
            )}
            {entry.content && (
              <p className="text-sm text-gray-700">{entry.content}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Feedback deadline info
  const deadline = tournament?.postRoundFeedbackDeadline
    ? new Date(tournament.postRoundFeedbackDeadline)
    : null;

  if (!editable) {
    return (
      <div className="text-center py-12">
        <div className="text-3xl mb-2">&#128221;</div>
        <h3 className="font-medium text-gray-700 mb-1">Feedback Not Open</h3>
        <p className="text-sm text-gray-500">
          The post-round feedback form will open after the tournament is completed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-1">Post-Round Feedback</h3>
        <p className="text-sm text-gray-500 mb-4">
          Help us improve future tournaments with your feedback.
        </p>

        {deadline && (
          <p className="text-xs text-gray-400 mb-4">
            Deadline: {deadline.toLocaleString()}
          </p>
        )}

        {/* Overall Rating */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Overall Rating</label>
          <StarRating value={form.overallRating} onChange={(v) => setForm((prev) => ({ ...prev, overallRating: v }))} />
        </div>

        {/* Course Condition */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Course Condition</label>
          <StarRating
            value={form.courseCondition.rating}
            onChange={(v) => setForm((prev) => ({ ...prev, courseCondition: { ...prev.courseCondition, rating: v } }))}
          />
          <textarea
            value={form.courseCondition.comment}
            onChange={(e) => setForm((prev) => ({ ...prev, courseCondition: { ...prev.courseCondition, comment: e.target.value } }))}
            placeholder="Any comments about course conditions..."
            rows={2}
            className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>

        {/* Pace of Play Experience */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Pace of Play</label>
          <StarRating
            value={form.paceExperience.rating}
            onChange={(v) => setForm((prev) => ({ ...prev, paceExperience: { ...prev.paceExperience, rating: v } }))}
          />
          <textarea
            value={form.paceExperience.comment}
            onChange={(e) => setForm((prev) => ({ ...prev, paceExperience: { ...prev.paceExperience, comment: e.target.value } }))}
            placeholder="How was the pace of play?"
            rows={2}
            className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>

        {/* Tournament Organization */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Tournament Organization</label>
          <StarRating
            value={form.tournamentOrganization.rating}
            onChange={(v) => setForm((prev) => ({ ...prev, tournamentOrganization: { ...prev.tournamentOrganization, rating: v } }))}
          />
          <textarea
            value={form.tournamentOrganization.comment}
            onChange={(e) => setForm((prev) => ({ ...prev, tournamentOrganization: { ...prev.tournamentOrganization, comment: e.target.value } }))}
            placeholder="Tee time accuracy, communication, etc."
            rows={2}
            className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>

        {/* General Comments */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">General Comments</label>
          <textarea
            value={form.generalComments}
            onChange={(e) => setForm((prev) => ({ ...prev, generalComments: e.target.value }))}
            placeholder="Anything else you'd like to share..."
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 bg-green-600 text-white rounded-xl font-medium disabled:opacity-50 hover:bg-green-700 transition-colors"
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
};

export default PortalFeedbackTab;
